import React, { useState, useEffect, useCallback, memo } from "react";
import { Card, Select, DatePicker, Button, Table, message } from "antd";
import moment from "moment";
import {
  getStatesByRole,
  getCleaningAuditRoleId,
  getFacilitiesByState,
  getReportTypes,
  getCleaningAuditReports,
  getPresignedReportUrl,
} from "./cleaningAuditApi";

const { Option } = Select;

// Matches reports/index.js's actual live behavior: the per-report-type table
// renderers in that file are commented out in the original source, so what
// users actually see after searching is the plain report history list
// (facility, report name, date, download) — reproduced here as-is.
const Reports = () => {
  const [states, setStates] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [reportTypes, setReportTypes] = useState([]);
  const [stateId, setStateId] = useState(null);
  const [facilityId, setFacilityId] = useState(null);
  const [reportType, setReportType] = useState(null);
  const [month, setMonth] = useState(moment());
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const roleId = await getCleaningAuditRoleId();
        const [statesRes, reportTypesRes] = await Promise.all([getStatesByRole(roleId), getReportTypes()]);
        setStates(!statesRes.error && Array.isArray(statesRes.result?.result) ? statesRes.result.result : []);
        setReportTypes(!reportTypesRes.error && Array.isArray(reportTypesRes.result?.result) ? reportTypesRes.result.result : []);
      } catch (e) {
        setStates([]);
        setReportTypes([]);
        message.error({ key: "cleaning-audit-connect", content: "Couldn't connect to Cleaning Audit. Please try again shortly." });
      }
    })();
  }, []);

  const onStateChange = useCallback(async (value) => {
    setStateId(value);
    setFacilityId(null);
    setFacilities([]);
    if (!value) return;
    const { error, result } = await getFacilitiesByState(value);
    setFacilities(!error && Array.isArray(result?.result) ? result.result : []);
  }, []);

  const search = useCallback(async () => {
    if (!stateId || !facilityId || !reportType) {
      message.error("Please select a state, facility and report type first.");
      return;
    }
    setLoading(true);
    setSearched(true);
    const { error, result } = await getCleaningAuditReports({
      date: month.format("MM/YYYY"),
      facility_id: facilityId,
      report_type: Number(reportType),
    });
    setRows(!error && Array.isArray(result?.result) ? result.result : []);
    setLoading(false);
  }, [stateId, facilityId, reportType, month]);

  const download = useCallback(async (pdfUrl) => {
    const { error, result } = await getPresignedReportUrl(pdfUrl);
    if (error || !result?.success) {
      message.error("Couldn't open that report.");
      return;
    }
    window.open(result.result, "_blank", "noopener,noreferrer");
  }, []);

  const columns = [
    { title: "Facility Name", dataIndex: "facility_name", key: "facility_name" },
    { title: "Report Name", dataIndex: "name", key: "name" },
    {
      title: "Report Date",
      dataIndex: "report_date",
      key: "report_date",
      // Raw DB date from the API; shown as DD-MM-YYYY (left as-is if it can't be read as a date).
      render: (v) => (v && moment(v).isValid() ? moment(v).format("DD-MM-YYYY") : v || "-"),
    },
    {
      title: "PDF Report",
      key: "download",
      render: (_, row) => (
        <Button size="small" onClick={() => download(row.pdf_url)}>Download Report</Button>
      ),
    },
  ];

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div>
            <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>Month</div>
            <DatePicker
              picker="month"
              value={month}
              onChange={(d) => d && setMonth(d)}
              allowClear={false}
              format="MM-YYYY"
            />
          </div>
          <div>
            <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>State</div>
            <Select
              showSearch
              optionFilterProp="children"
              placeholder="Select a state"
              style={{ width: 200 }}
              value={stateId}
              onChange={onStateChange}
            >
              {states.map((s) => <Option key={s.state_id} value={s.state_id}>{s.state_name}</Option>)}
            </Select>
          </div>
          <div>
            <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>Facility Name</div>
            <Select
              showSearch
              optionFilterProp="children"
              placeholder="Select a facility"
              style={{ width: 220 }}
              value={facilityId}
              onChange={setFacilityId}
              disabled={!stateId}
              notFoundContent={stateId ? "No facilities for this state" : "Select a state first"}
            >
              {facilities.map((f) => <Option key={f.facility_id} value={f.facility_id}>{f.facility_name}</Option>)}
            </Select>
          </div>
          <div>
            <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>Report Type</div>
            <Select
              placeholder="Select a report type"
              style={{ width: 260 }}
              value={reportType}
              onChange={setReportType}
            >
              {reportTypes.map((r) => <Option key={r.report_type_id} value={r.report_type_id}>{r.name}</Option>)}
            </Select>
          </div>
          <Button type="primary" loading={loading} onClick={search}>Search</Button>
        </div>
      </Card>

      {searched && (
        <Card title="Audit Report History">
          <Table
            rowKey={(_, i) => i}
            columns={columns}
            dataSource={rows}
            loading={loading}
            pagination={{ pageSize: 20 }}
            locale={{ emptyText: "No reports found for these filters." }}
          />
        </Card>
      )}
    </div>
  );
};

export default memo(Reports);
